/**
 * 신규 API(`ggc-be-staging-...`)로의 마이그레이션 피처 플래그.
 *
 * 참고: docs/swagger-api-migration-plan.ko.md
 *
 * 현재 본 앱에서 마이그레이션 대상은 외부 인증(`/influencer/auth/*`)뿐.
 * BE 세션 정합성이 검증되기 전까지는 명시적 opt-in이 안전 — 그러나
 * influencer auth는 admin과 달리 레거시와 동일한 `usersession.sid`
 * 쿠키를 사용하므로 (ggc-be/src/api/index.ts 참고) 스테이징 호스트네임에서는
 * 기본 ON으로 둘 수 있다.
 */

/**
 * 빌드 환경(DEV/PROD)과 무관하게 새 API + 직접 OAuth 호출을 활성화하는 배포 도메인.
 * BE에서 DOMAIN_ZOKU를 해당 도메인으로 설정해야 실제 인증 연동이 된다.
 *
 * - influencer2-staging: api-staging.ggcontent.com (isStagingBackend() → true)
 * - influencer2:          api.ggcontent.com        (isStagingBackend() → false)
 */
const DEPLOYED_NEW_API_HOSTS = [
  'influencer2-staging.ggcontent.com',
  'influencer2.ggcontent.com',
] as const;

/** DEV 빌드에서만 새 API를 활성화하는 로컬 호스트 */
const DEV_NEW_API_HOSTS = [
  'localhost',
] as const;


/**
 * 백엔드 OAuth 직접 호출 + 실제 인증 연동이 가능한 도메인인지 여부.
 * Login/Profile 등에서 direct: true 여부 결정에 사용한다.
 */
export function isDirectAuthMode(): boolean {
  if (typeof window === 'undefined') return false;
  return DEPLOYED_NEW_API_HOSTS.some((h) => window.location.hostname === h);
}

function parseBool(value: string | undefined): boolean | undefined {
  if (value == null || value === '') return undefined;
  const v = value.toLowerCase();
  if (v === 'true' || v === '1' || v === 'yes' || v === 'on') return true;
  if (v === 'false' || v === '0' || v === 'no' || v === 'off') return false;
  return undefined;
}

function hostDefault(): boolean {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  // 배포 staging 도메인: PROD 빌드여도 새 API 활성화
  if (DEPLOYED_NEW_API_HOSTS.some((h) => h === hostname)) return true;
  if (import.meta.env.PROD) return false;
  return DEV_NEW_API_HOSTS.some((h) => h === hostname);
}

export function useNewInfluencerAuth(): boolean {
  const override = parseBool(import.meta.env.VITE_USE_NEW_INFLUENCER_AUTH);
  if (override !== undefined) return override;
  const global = parseBool(import.meta.env.VITE_USE_NEW_API);
  if (global !== undefined) return global;
  return hostDefault();
}
